// @ts-strict-ignore
import pick from 'lodash/pick';
import isString from 'lodash/isString';

import {PropertyType} from 'common/report/constants';
import {
  INSIGHTS_BOOLEAN_TYPES,
  INSIGHTS_COHORT_TYPE,
  INSIGHTS_DATE_RANGE_TYPES,
  INSIGHTS_LIST_TYPES,
  INSIGHTS_NUMBER_TYPES,
  INSIGHTS_STRING_TYPES,
  insightsFilterAttrsFromPropertyFilter,
  insightsFilterToPropertyFilter,
} from 'common/widgets/property-filter-menu/models/insights-filter';
import {ListOperator, ObjectOperator} from 'common/widgets/property-filter-menu/models/types';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {ProfileHistoryOptions} from 'common/types/reports/query';
import {isMultiAttributionBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {handleNotSetValueForDatetimeFilters} from 'common/widgets/property-filter-menu/utils';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {InsightsResourceType} from 'common/types/reports/bookmark';

import {FilterClause as FilterClauseType} from '../util/types';
import {DATASET_MIXPANEL} from './constants';
import {ClauseType} from './types';

const FILTER_OPERATORS = {
  [PropertyType.String]: [
    INSIGHTS_STRING_TYPES.CONTAINS,
    INSIGHTS_STRING_TYPES.DOES_NOT_CONTAIN,
    INSIGHTS_STRING_TYPES.EQUALS,
    INSIGHTS_STRING_TYPES.DOES_NOT_EQUAL,
    INSIGHTS_STRING_TYPES.IS_SET,
    INSIGHTS_STRING_TYPES.IS_NOT_SET,
  ],
  [PropertyType.Number]: [
    INSIGHTS_NUMBER_TYPES.IS_BETWEEN,
    INSIGHTS_NUMBER_TYPES.IS_NOT_BETWEEN,
    INSIGHTS_NUMBER_TYPES.IS_AT_LEAST,
    INSIGHTS_NUMBER_TYPES.IS_AT_MOST,
    INSIGHTS_NUMBER_TYPES.IS_GREATER_THAN,
    INSIGHTS_NUMBER_TYPES.IS_LESS_THAN,
    INSIGHTS_NUMBER_TYPES.IS_EQUAL_TO,
    INSIGHTS_NUMBER_TYPES.IS_NOT_EQUAL_TO,
    INSIGHTS_NUMBER_TYPES.IS_SET,
    INSIGHTS_NUMBER_TYPES.IS_NOT_SET,
  ],
  [PropertyType.Datetime]: [
    INSIGHTS_DATE_RANGE_TYPES.WAS_ON,
    INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_ON,
    INSIGHTS_DATE_RANGE_TYPES.WAS_BETWEEN,
    INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_BETWEEN,
    INSIGHTS_DATE_RANGE_TYPES.WAS_LESS_THAN,
    INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE,
    INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_IN_THE,
    INSIGHTS_DATE_RANGE_TYPES.WAS_BEFORE,
    INSIGHTS_DATE_RANGE_TYPES.WAS_SINCE,
    INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE_NEXT,
  ],
  [PropertyType.Boolean]: [INSIGHTS_BOOLEAN_TYPES.IS_TRUE, INSIGHTS_BOOLEAN_TYPES.IS_FALSE],
  [PropertyType.List]: [INSIGHTS_LIST_TYPES.CONTAINS, INSIGHTS_LIST_TYPES.DOES_NOT_CONTAIN],
  [PropertyType.Object]: [ObjectOperator.Equals, ObjectOperator.Set],
};

/** @deprecated use POJO */
export class FilterClause {
  static TYPE: typeof ClauseType.Filter = ClauseType.Filter;
  static defaultCohortsAttrs() {
    return {
      dataset: DATASET_MIXPANEL,
      filterType: PropertyType.List,
      filterOperator: ListOperator.Contains,
      filterValue: [],
      resourceType: InsightsResourceType.Events,
      value: INSIGHTS_COHORT_TYPE,
    };
  }

  static defaultCohortsClause() {
    return new FilterClause(FilterClause.defaultCohortsAttrs());
  }

  declare dataset: FilterClauseType[`dataset`];
  declare filterType: FilterClauseType[`filterType`];
  declare filterOperator: FilterClauseType[`filterOperator`];
  declare filterValue: FilterClauseType[`filterValue`];
  declare resourceType: FilterClauseType[`resourceType`];
  declare value: FilterClauseType[`value`];
  // it's always null
  declare profileType: FilterClauseType[`profileType`];
  declare search: FilterClauseType[`search`];
  declare dataGroupId: FilterClauseType[`search`];

  declare behavior?: FilterClauseType[`behavior`];
  declare defaultType: FilterClauseType[`defaultType`];
  declare propertyObjectKey?: FilterClauseType[`propertyObjectKey`];
  declare filterJoinType?: FilterClauseType[`filterJoinType`];
  declare listInclusion?: FilterClauseType[`listInclusion`];
  declare listQuantifier?: FilterClauseType[`listQuantifier`];
  declare listItemFilters?: FilterClauseType[`listItemFilters`];
  declare filterDateUnit?: FilterClauseType[`filterDateUnit`];
  declare customPropertyId?: FilterClauseType[`customPropertyId`];
  declare isHidden?: FilterClauseType[`isHidden`];
  declare isInherited?: FilterClauseType[`isInherited`];
  declare customProperty?: FilterClauseType[`customProperty`];
  declare determiner?: FilterClauseType[`determiner`];
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  declare tempDataGroupId?: FilterClauseType[`tempDataGroupId`];
  declare profileHistoryOptions?: ProfileHistoryOptions;

  declare scdEvent?: FilterClauseType[`scdEvent`];
  declare scdOptions?: FilterClauseType[`scdOptions`];
  declare experiments?: FilterClauseType[`experiments`];

  // for funnel step ttc step selection
  declare startStepNumber?: FilterClauseType[`startStepNumber`];
  declare endStepNumber?: FilterClauseType[`endStepNumber`];

  constructor(attrs: Partial<FilterClauseType>) {
    this.dataset = attrs.dataset;
    this.value = attrs.value || null;
    this.resourceType = attrs.resourceType || InsightsResourceType.Events;
    this.profileType = attrs.profileType || null;
    this.search = attrs.search || ``;
    this.dataGroupId = attrs.dataGroupId || null;

    if (attrs.isHidden) {
      this.isHidden = attrs.isHidden;
    }

    if (attrs.isInherited) {
      this.isInherited = attrs.isInherited;
    }

    if (!attrs.customPropertyId && attrs.customProperty) {
      this.customProperty = attrs.customProperty;
    }
    if (attrs.customPropertyId && !isPersistedCustomProperty(attrs.customPropertyId) && attrs.customProperty) {
      this.customProperty = attrs.customProperty;
    }
    if (attrs.customPropertyId) {
      this.customPropertyId = attrs.customPropertyId;
    }
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }
    if (`startStepNumber` in attrs) {
      this.startStepNumber = attrs.startStepNumber;
    }
    if (`endStepNumber` in attrs) {
      this.endStepNumber = attrs.endStepNumber;
    }

    this.determiner = attrs.determiner;
    this.filterType = attrs.filterType;
    this.filterOperator = attrs.filterOperator;
    this.filterValue = attrs.filterValue === 0 || attrs.filterValue ? attrs.filterValue : null;
    this.propertyObjectKey = attrs.propertyObjectKey || null;

    if (`scdEvent` in attrs) {
      this.scdEvent = attrs.scdEvent;
    }
    if (`scdOptions` in attrs) {
      this.scdOptions = attrs.scdOptions;
    }
    if (`experiments` in attrs) {
      this.experiments = attrs.experiments;
    }

    if (attrs.behavior) {
      this.behavior = attrs.behavior;
    }

    if (attrs.filterJoinType) {
      this.filterJoinType = attrs.filterJoinType;
    }

    if (attrs.listInclusion) {
      this.listInclusion = attrs.listInclusion;
    }

    if (attrs.listQuantifier) {
      this.listQuantifier = attrs.listQuantifier;
    }

    if (attrs.listItemFilters) {
      this.listItemFilters = attrs.listItemFilters;
    }

    if (attrs.defaultType) {
      this.defaultType = attrs.defaultType;
    }

    if (attrs.filterDateUnit) {
      this.filterDateUnit = attrs.filterDateUnit;
    }

    if (attrs.profileHistoryOptions) {
      this.profileHistoryOptions = attrs.profileHistoryOptions;
    }

    if (this.value === INSIGHTS_COHORT_TYPE) {
      // ensure events resource type for a cohort filter
      this.resourceType = InsightsResourceType.Events;
    }

    this.filterValue = handleNotSetValueForDatetimeFilters(this.filterValue, this.defaultType);
  }

  static fromPropertyFilter(propertyFilter) {
    const attrs = insightsFilterAttrsFromPropertyFilter(propertyFilter);
    return new FilterClause(attrs);
  }

  public toPropertyFilter() {
    return insightsFilterToPropertyFilter(this);
  }

  get attrs() {
    return pick(this, [
      `dataset`,
      `customProperty`,
      `customPropertyId`,
      `value`,
      `resourceType`,
      `profileType`,
      `search`,
      `dataGroupId`,
      `tempDataGroupId`,
      `behavior`,
      `filterType`,
      `filterJoinType`,
      `listInclusion`,
      `listQuantifier`,
      `listItemFilters`,
      `defaultType`,
      `filterOperator`,
      `filterValue`,
      `filterDateUnit`,
      `propertyObjectKey`,
      `profileHistoryOptions`,
      `scdEvent`,
      `scdOptions`,
      `experiments`,
      `startStepNumber`,
      `endStepNumber`,
      `isHidden`,
      `isInherited`,
    ]);
  }

  toBookmarkData() {
    return this.attrs;
  }

  serialize() {
    return this.attrs;
  }

  extend(attrs: Partial<FilterClauseType>) {
    return new FilterClause({...this.attrs, ...attrs});
  }

  toUrlData() {
    return this.attrs;
  }

  get valid() {
    if (!this.filterType || !this.filterOperator) {
      return false;
    }

    // list item filters ignore their operators
    const isComplexListFilter = !!this.listQuantifier && this.filterType === PropertyType.Object;

    const isFilterTypeValid = Object.values(PropertyType).includes(this.filterType);
    const isDefaultFilterTypeValid = !this.defaultType || Object.values(PropertyType).includes(this.defaultType);
    const isFilterOperatorValid =
      isComplexListFilter || FILTER_OPERATORS[this.filterType].includes(this.filterOperator);

    const isResourceTypeValid = Object.values(InsightsResourceType).includes(this.resourceType);
    const isSearchValid = isString(this.search);
    const isCustomPropertyValid = !this.isCustomProperty || this.isCustomPropertyValid();

    return (
      isResourceTypeValid &&
      isSearchValid &&
      isCustomPropertyValid &&
      isFilterTypeValid &&
      isDefaultFilterTypeValid &&
      isFilterOperatorValid
    );
  }

  isCustomPropertyValid() {
    return (
      this.customPropertyId ||
      (this.customProperty.name && this.customProperty.composedProperties && this.customProperty.displayFormula)
    );
  }

  mapForAttribution(showClause) {
    if (!isMultiAttributionBehavior(this.behavior)) {
      return this;
    }

    if (showClause.measurement.multiAttribution) {
      return this.extend({
        behavior: {
          ...this.behavior,
          multiAttribution: showClause.measurement.multiAttribution,
        },
      });
    }

    return this.extend({
      behavior: null,
      filterJoinType: null,
      listInclusion: null,
      listQuantifier: null,
      value: this.behavior.property.value,
    });
  }

  get isCustomProperty() {
    return !!this.customProperty || !!this.customPropertyId;
  }

  get isCohortsFilter() {
    return this.value === INSIGHTS_COHORT_TYPE;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  getInlineCohort() {
    const value = Array.isArray(this.filterValue) ? this.filterValue[0] : this.filterValue;

    if (!value || !value.cohort) {
      return null;
    }

    return value.cohort.raw_cohort ? value.cohort : null;
  }

  trackingData() {
    return {
      ...this.attrs,
      ...(this.isCohortsFilter
        ? {
            filterType: InsightsResourceType.Cohorts,
            filterOperator: this.filterOperator === `contains` ? `users in` : `users not in`,
          }
        : {}),
    };
  }
}

export function isBehaviorFilterClause(
  filter: FilterClauseType,
): filter is FilterClauseType & {behavior: FilterClauseType[`behavior`]} {
  return !!filter.behavior;
}

export function isTTCFilterClause(
  filter: FilterClauseType,
): filter is FilterClauseType & {startStepNumber: FilterClauseType[`startStepNumber`]} {
  return filter.value === FUNNELS_TIME_TO_CONVERSION.value;
}
